import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import { getGuideline } from '../../data/discordGuidelines'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordGuidelinesPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')

  const GuidelineItem = ({ number }: { number: string }) => {
    const guideline = getGuideline(number)
    if (!guideline) return null
    const id = `guideline-${number}`

    return (
      <div id={id} className={styles.guidelineWrapper}>
        <CopyLinkButton sectionId={id} language={language} className={styles.copyBtn} />
        <div className={styles.guidelineContent}>
          <strong>
            {guideline.number}. {guideline.title[language]}
          </strong>
          <span>{guideline.content[language]}</span>
        </div>
      </div>
    )
  }

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Discord Community Guidelines</h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze překlad oficiálních
                Discord Community Guidelines dostupných na{' '}
                <a href="https://discord.com/guidelines" target="_blank" rel="noopener noreferrer">
                  discord.com/guidelines
                </a>
                .
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with Discord Inc. This is only a translation of the official
                Discord Community Guidelines available at{' '}
                <a href="https://discord.com/guidelines" target="_blank" rel="noopener noreferrer">
                  discord.com/guidelines
                </a>
                .
              </p>
            </div>
          </div>
          <div className={styles.linkLegend}>
            <h4>{language === 'cs' ? 'Legenda:' : 'Legend:'}</h4>
            <div className={styles.legendItem}>
              <span className={`${styles.legendLink} ${styles.discord}`}>
                {language === 'cs' ? 'Modré odkazy' : 'Blue links'}
              </span>
              <span>
                = {language === 'cs' ? 'oficiální Discord stránky' : 'official Discord pages'}
              </span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendLink} ${styles.internal}`}>
                {language === 'cs' ? 'Žluté odkazy' : 'Yellow links'}
              </span>
              <span>= {language === 'cs' ? 'naše přeložené stránky' : 'our translated pages'}</span>
            </div>
          </div>
        </>
      }
    >
      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí pravidel</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Respektujte se navzájem</h3>
              <ul>
                <li>Žádné obtěžování, šikana ani vyhrožování</li>
                <li>Žádné sdílení osobních údajů bez souhlasu</li>
                <li>Žádné nenávistné projevy nebo extremismus</li>
                <li>Přísná ochrana dětí</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Respektujte Discord</h3>
              <ul>
                <li>Žádný spam nebo self-boty</li>
                <li>Žádná manipulace s platformou</li>
                <li>Žádné dezinformace nebo falešná identita</li>
                <li>Žádné hackování nebo podvody</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Dodržujte zákon</h3>
              <ul>
                <li>Respektujte autorská práva</li>
                <li>Žádný prodej regulovaného zboží</li>
                <li>Žádné nelegální hazardní hry</li>
                <li>Žádné jiné nezákonné aktivity</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Jednotlivá pravidla najdete níže; úplné znění včetně
            úvodu a podmínek jejich uplatňování je v originálu.
          </p>
        </details>
      )}

      {/* Guidelines Body */}
      <div className={styles.guidelinesBody}>
        <p>
          {language === 'cs'
            ? 'Vytvořili jsme Discord jako nejlepší místo pro povídání, setkávání a zábavu s přáteli online. Naše komunitní pravidla zajišťují, že se každý může vyjádřit a najít komunitu — ale ne na úkor ostatních.'
            : 'We created Discord to be the best place to talk, hang out, and have fun with friends online. Our Community Guidelines ensure everyone can express themselves and find community — but not at the expense of anyone else.'}
        </p>

        <p>
          {language === 'cs' ? (
            <>
              Tato pravidla vysvětlují, co na Discordu není povoleno. Každý na Discordu musí tato
              pravidla dodržovat a vztahují se na všechny části naší platformy. Vývojáři musí
              dodržovat také{' '}
              <a href="https://discord.com/developers/docs/policies-and-agreements/developer-terms-of-service">
                Podmínky služby pro vývojáře
              </a>
              .
            </>
          ) : (
            <>
              These Guidelines explain what isn't allowed on Discord. Everyone on Discord must
              follow these rules. Developers must also follow the{' '}
              <a href="https://discord.com/developers/docs/policies-and-agreements/developer-terms-of-service">
                Developer Terms of Service
              </a>
              .
            </>
          )}
        </p>

        <p className={styles.editorNote}>
          {language === 'cs' ? (
            <>
              Poznámka Allcomu: úvod a závěr jsou zkráceny. Podrobnosti o uplatňování pravidel a
              souvisejících podmínkách najdete v{' '}
              <a href="https://discord.com/guidelines">oficiálním originálu</a>.
            </>
          ) : (
            <>
              Allcom note: the introduction and conclusion are abridged. See the{' '}
              <a href="https://discord.com/guidelines">official original</a> for enforcement details
              and related terms.
            </>
          )}
        </p>

        <p>
          {language === 'cs' ? (
            <>
              Pokud narazíte na obsah, který porušuje tato pravidla,{' '}
              <a href="/discord/safety/reporting-abusive-behavior-to-discord">
                nahlaste nám to prosím
              </a>
              .
            </>
          ) : (
            <>
              If you come across content that appears to break these Guidelines, please{' '}
              <a href="/discord/safety/reporting-abusive-behavior-to-discord">report it to us</a>.
            </>
          )}
        </p>

        {/* Respect Each Other */}
        <div className={styles.guidelinesSection}>
          <h2>{language === 'cs' ? 'Respektujte se navzájem' : 'Respect Each Other'}</h2>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
            <GuidelineItem key={n} number={String(n)} />
          ))}
        </div>

        {/* Respect Discord */}
        <div className={styles.guidelinesSection}>
          <h2>{language === 'cs' ? 'Respektujte Discord' : 'Respect Discord'}</h2>
          {[13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map((n) => (
            <GuidelineItem key={n} number={String(n)} />
          ))}
        </div>

        {/* Follow the Law */}
        <div className={styles.guidelinesSection}>
          <h2>{language === 'cs' ? 'Dodržujte zákon' : 'Follow the Law'}</h2>
          {[24, 25, 26, 27].map((n) => (
            <GuidelineItem key={n} number={String(n)} />
          ))}
        </div>

        <p>
          {language === 'cs' ? (
            <>
              Pokud uvidíte jakýkoli obsah, který porušuje tato pravidla, naše{' '}
              <a href="/discord/terms">Podmínky služby</a> nebo naše další zásady,{' '}
              <a href="/discord/safety/reporting-abusive-behavior-to-discord">
                nahlaste nám to prosím
              </a>
              .
            </>
          ) : (
            <>
              If you see any content that violates these Guidelines, our{' '}
              <a href="/discord/terms">Terms of Service</a>, or our other policies, please{' '}
              <a href="/discord/safety/reporting-abusive-behavior-to-discord">report it to us</a>.
            </>
          )}
        </p>

        <p>
          {language === 'cs'
            ? 'Tato pravidla se budou v průběhu času nadále vyvíjet. Vždy vynaložíme maximální úsilí, abychom vás upozornili, když tato pravidla aktualizujeme. Děkujeme, že děláte svou část.'
            : 'These Guidelines will continue to evolve over time. We will always make our best effort to notify you when we update these Guidelines. Thanks for doing your part.'}
        </p>
      </div>
    </DiscordDocument>
  )
}

export default DiscordGuidelinesPage
